import { ROUTES } from '@/constants/app';
import { useRegister } from '@/hooks/useAuth';
import { RegisterData } from '@/types/auth.types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff, Link2 } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { DataProtectionLink } from '../DataProtectionLink';

const registerSchema = z.object({
  email: z
    .string()
    .email('auth.validation.email.invalid')
    .min(1, 'auth.validation.email.required'),
  password: z
    .string()
    .min(8, 'auth.validation.password.minLength')
    .regex(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'auth.validation.password.strength'
    ),
  firstName: z.string().min(2, 'auth.validation.firstName.minLength'),
  lastName: z.string().min(2, 'auth.validation.lastName.minLength'),
  openPayeUsername: z
    .string()
    .min(1, 'auth.validation.openPaye.username.required'),
  openPayePassword: z
    .string()
    .min(1, 'auth.validation.openPaye.password.required'),
  companyName: z.string().min(1, 'auth.validation.companyName.required'),
  preferredLanguage: z.enum(['fr', 'en']),
  timezone: z.string().optional(),
  acceptTerms: z.boolean().refine(val => val === true, {
    message: 'auth.validation.terms.required',
  }),
});

type RegisterFormData = Omit<RegisterData, 'confirmPassword'>;

export const RegisterForm = () => {
  const { t } = useTranslation();
  const register = useRegister();
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const { i18n } = useTranslation();

  // Detect user's timezone automatically
  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      // Fallback to UTC if timezone detection fails
      return 'UTC';
    }
  };

  const {
    register: registerField,
    handleSubmit,
    formState: { errors, isSubmitting },
    trigger,
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      preferredLanguage: 'fr',
      openPayePassword: '',
      acceptTerms: false,
      timezone: getUserTimezone(),
    },
  });

  const onSubmit = async (data: RegisterFormData) => {
    try {
      await register.mutateAsync({
        ...data,
        // L'API conserve ce champ historique, mais l'utilisateur ne saisit
        // son mot de passe qu'une seule fois.
        confirmPassword: data.password,
        preferredLanguage: i18n.language.startsWith('fr') ? 'fr' : 'en',
        timezone: getUserTimezone(), // Ensure timezone is always included
      });
    } catch {
      // Error is handled by the useRegister hook with toast
    }
  };

  const handleNextStep = async () => {
    // Valider uniquement les champs de l'étape 1
    const valid = await trigger([
      'firstName',
      'lastName',
      'email',
      'password',
      'acceptTerms',
    ]);
    if (valid) {
      setStep(2);
    }
  };

  const prevStep = () => setStep(1);

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-6"
      autoComplete="off"
    >
      {/* Step 1 - always in DOM, hidden via CSS when step !== 1 */}
      <div className={step === 1 ? 'space-y-5' : 'hidden'}>
        <h2 className="sr-only">{t('auth.register.step1.title')}</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="firstName"
              className="block text-sm font-medium text-neutral-700"
            >
              {t('auth.register.step1.firstName')}
            </label>
            <input
              {...registerField('firstName')}
              type="text"
              id="firstName"
              autoComplete="given-name"
              className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
            />
            {errors.firstName && (
              <p className="text-error-600 mt-1 text-sm">
                {t(errors.firstName.message as string)}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="lastName"
              className="block text-sm font-medium text-neutral-700"
            >
              {t('auth.register.step1.lastName')}
            </label>
            <input
              {...registerField('lastName')}
              type="text"
              id="lastName"
              autoComplete="family-name"
              className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
            />
            {errors.lastName && (
              <p className="text-error-600 mt-1 text-sm">
                {t(errors.lastName.message as string)}
              </p>
            )}
          </div>
        </div>

        <div>
          <label
            htmlFor="email"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('auth.register.step1.email')}
          </label>
          <input
            {...registerField('email')}
            type="email"
            id="email"
            autoComplete="email"
            className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
          />
          {errors.email && (
            <p className="text-error-600 mt-1 text-sm">
              {t(errors.email.message as string)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="password"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('auth.register.step1.password')}
          </label>
          <div className="relative mt-1">
            <input
              {...registerField('password')}
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              className="focus:border-primary-500 focus:ring-primary-500 block w-full rounded-md border border-neutral-300 px-3 py-2 pr-11 shadow-sm focus:ring-1 focus:outline-none"
            />
            <button
              type="button"
              onClick={() => setShowPassword(current => !current)}
              className="absolute top-1/2 right-0 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-md text-neutral-500 transition-colors hover:text-neutral-700 focus:ring-2 focus:outline-none"
              aria-label={
                showPassword
                  ? t('auth.register.step1.hidePassword')
                  : t('auth.register.step1.showPassword')
              }
            >
              {showPassword ? (
                <EyeOff className="h-5 w-5" aria-hidden="true" />
              ) : (
                <Eye className="h-5 w-5" aria-hidden="true" />
              )}
            </button>
          </div>
          {errors.password && (
            <p className="text-error-600 mt-1 text-sm">
              {t(errors.password.message as string)}
            </p>
          )}
        </div>

        {/* Checkbox d'acceptation des conditions */}
        <div>
          <div className="flex items-start space-x-3">
            <input
              {...registerField('acceptTerms')}
              type="checkbox"
              id="acceptTerms"
              className="text-primary-600 focus:ring-primary-500 mt-1 h-4 w-4 rounded border-neutral-300"
            />
            <div className="flex-1">
              <label
                htmlFor="acceptTerms"
                className="text-sm leading-relaxed text-neutral-700"
              >
                {t('auth.register.step1.acceptTerms.text')}{' '}
                <Link
                  to={ROUTES.LEGAL.TERMS}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline transition-colors hover:opacity-80"
                  style={{ color: 'var(--marque-accent)' }}
                >
                  {t('auth.register.step1.acceptTerms.terms')}
                </Link>{' '}
                {t('auth.register.step1.acceptTerms.and')}{' '}
                <Link
                  to={ROUTES.LEGAL.PRIVACY}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium underline transition-colors hover:opacity-80"
                  style={{ color: 'var(--marque-accent)' }}
                >
                  {t('auth.register.step1.acceptTerms.privacy')}
                </Link>
                .
              </label>
            </div>
          </div>
          {errors.acceptTerms && (
            <p className="text-error-600 mt-2 text-sm">
              {t(errors.acceptTerms.message as string)}
            </p>
          )}
        </div>

        <button
          type="button"
          onClick={handleNextStep}
          className="focus:ring-primary-500 flex min-h-11 w-full justify-center rounded-md px-4 py-2 text-sm font-medium shadow-sm transition-[filter] hover:brightness-90 focus:ring-2 focus:ring-offset-2 focus:outline-none"
          style={{
            backgroundColor: 'var(--marque-accent)',
            color: 'var(--marque-sur-accent)',
          }}
        >
          {t('auth.register.step1.continue')}
        </button>
      </div>

      {/* Step 2 - always in DOM, hidden via CSS when step !== 2 */}
      <div className={step === 2 ? 'space-y-5' : 'hidden'}>
        <h2 className="sr-only">{t('auth.register.step2.title')}</h2>
        <p className="flex items-center gap-2 text-sm text-neutral-600">
          <Link2 className="h-4 w-4 shrink-0" aria-hidden="true" />
          {t('auth.register.step2.openPayeSection.description')}
        </p>

        <div>
          <label
            htmlFor="openPayeUsername"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('auth.register.step2.openPayeUsername')}
          </label>
          <input
            {...registerField('openPayeUsername')}
            type="text"
            id="openPayeUsername"
            autoComplete="off"
            className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
          />
          {errors.openPayeUsername && (
            <p className="text-error-600 mt-1 text-sm">
              {t(errors.openPayeUsername.message as string)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="openPayePassword"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('auth.register.step2.openPayePassword')}
          </label>
          <input
            {...registerField('openPayePassword')}
            type="password"
            id="openPayePassword"
            autoComplete="off"
            className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
          />
          {errors.openPayePassword && (
            <p className="text-error-600 mt-1 text-sm">
              {t(errors.openPayePassword.message as string)}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="companyName"
            className="block text-sm font-medium text-neutral-700"
          >
            {t('auth.register.step2.companyName')}
          </label>
          <input
            {...registerField('companyName')}
            type="text"
            id="companyName"
            autoComplete="organization"
            placeholder={t('auth.register.step2.companyNamePlaceholder')}
            className="focus:border-primary-500 focus:ring-primary-500 mt-1 block w-full rounded-md border border-neutral-300 px-3 py-2 shadow-sm focus:ring-1 focus:outline-none"
          />
          {errors.companyName && (
            <p className="text-error-600 mt-1 text-sm">
              {t(errors.companyName.message as string)}
            </p>
          )}
          <p className="mt-1 text-xs text-neutral-500">
            {t('auth.register.step2.companyNameHint')}
          </p>
        </div>

        <div className="flex space-x-4">
          <button
            type="button"
            onClick={prevStep}
            className="focus:ring-primary-500 min-h-11 flex-1 rounded-md border border-neutral-300 bg-white px-4 py-2 text-sm font-medium text-neutral-700 shadow-sm hover:bg-neutral-50 focus:ring-2 focus:ring-offset-2 focus:outline-none"
          >
            {t('auth.register.step2.previous')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="focus:ring-primary-500 min-h-11 flex-1 rounded-md px-4 py-2 text-sm font-medium shadow-sm transition-[filter] hover:brightness-90 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:opacity-50"
            style={{
              backgroundColor: 'var(--marque-accent)',
              color: 'var(--marque-sur-accent)',
            }}
          >
            {isSubmitting
              ? t('common.loading')
              : t('auth.register.step2.submit')}
          </button>
        </div>
      </div>

      <DataProtectionLink />

      <div className="text-center text-sm">
        <span className="text-neutral-600">
          {t('auth.register.hasAccount')}
        </span>{' '}
        <Link
          to="/login"
          className="inline-flex min-h-11 items-center font-medium transition-colors hover:opacity-80"
          style={{ color: 'var(--marque-accent)' }}
        >
          {t('auth.register.login')}
        </Link>
      </div>
    </form>
  );
};
